import PropTypes from 'prop-types';
import React from 'react';
import Flexbox from 'flexbox-react';
import InfoMessages from 'components/core/help/InfoMessages';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessDataTable from 'components/core/ProcessDataTable';
import RulesTable from 'components/page/process/variableRetrieval/inputs/RulesTable';
import MappingsTable from 'components/page/process/variableRetrieval/inputs/MappingsTable';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import EntityHeader from 'components/core/EntityHeader';
import InputDatasetNameField from 'components/core/fields/InputDatasetNameField';
import InputOffsetsField from 'components/core/fields/InputOffsetsField';

export default class RetrievalRulesTable extends ProcessDataTable {
    static propTypes = {
        inputDatasetNames: PropTypes.array, // array of strings of the input datasets to include in the table
        hideToolbar: PropTypes.bool,
        highlightVariableName: PropTypes.string // specific variable name you want to highlight in the table
    };

    constructor(props) {
        super(props);
        this.COL_NAME = 'name';
        this.COL_RULES = 'rules';
        this.COL_MAPPINGS = 'mappings';

        this.state.expandedColumns = {};
        this.state.expandedColumns[this.COL_NAME] = true;
        this.state.expandedColumns[this.COL_RULES] = true;
        this.state.expandedColumns[this.COL_MAPPINGS] = true;
    }

    onImportWizClick = () => {
        ComponentUtils.openImportWizardModal(this.props.process.name);
    };

    toggleExpand = (colId) => {
        const expanded = this.state.expandedColumns[colId] ? false : true;
        const expandedColumns = Object.assign({}, this.state.expandedColumns);
        expandedColumns[colId] = expanded;
        this.setState({ expandedColumns });
    };

    onDeleteInputDatasetsClick = () => {
        // Delete the input datasets represented by each row
        ComponentUtils.openDeleteEntitiesModal(this.props.process, 'variable_retrieval.input_datasets', this.state.selectedItems,
            this.validator.canInputDatasetsBeDeleted);
        this.clearSelection();
    };

    renderExpandableLabel(colId, label) {
        const expanded = this.state.expandedColumns[colId];
        const className = expanded ? 'expanded' : 'collapsed';
        return (
            <Flexbox flexDirection="row" className={className} flexGrow={1} alignItems="flex-end">
                <div className="collapsible-arrow" />
                <div>
                    {label}
                </div>
            </Flexbox>
        );
    }

    renderNameCell(inputDatasetName) {
        if (this.state.expandedColumns[this.COL_NAME]) {
            return (
                <InputDatasetNameField
                    process={this.props.process}
                    processErrors={this.props.processErrors}
                    hasWritePermissions={this.props.hasWritePermissions}
                    inputDatasetName={inputDatasetName}
                />
            );
        } else {
            return <div />;
        }
    }

    renderRulesTable(inputDatasetName) {
        if (this.state.expandedColumns[this.COL_RULES]) {
            return (
                <Flexbox flexDirection="column" flexGrow={1}>
                    <Flexbox className="entity-box" flexDirection="row" flexGrow={1} width="100%" padding="0 10px" marginBottom="0">
                        <RulesTable
                            className="rules-table"
                            tableClassName="table table-bordered"
                            process={this.props.process}
                            inputDatasetName={inputDatasetName}
                            dods={this.props.dods}
                            dodsById={this.props.dodsById}
                            siteFacilities={this.props.siteFacilities}
                            hasWritePermissions={this.props.hasWritePermissions}
                            processErrors={this.props.processErrors}
                        />
                    </Flexbox>
                    <Flexbox>
                        <InputOffsetsField
                            inputDatasetName={inputDatasetName}
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            hasWritePermissions={this.props.hasWritePermissions}
                        />
                    </Flexbox>
                </Flexbox>
            );
        } else {
            return <div />;
        }
    }

    renderMappingsTable(inputDatasetName) {
        if (this.state.expandedColumns[this.COL_MAPPINGS]) {

            const hideToolbar = this.props.hideToolbar ? true : false;
            return (
                <Flexbox flexDirection="row" flexGrow={1}>
                    <Flexbox className="entity-box" flexDirection="column" flexGrow={1} width="100%" padding="0 10px">
                        <Flexbox flexDirection="row" flexGrow={1} width="100%">
                            <MappingsTable
                                className="mappings-table"
                                tableClassName="table table-bordered"
                                process={this.props.process}
                                inputDatasetName={inputDatasetName}
                                hideToolbar={hideToolbar}
                                disableSelection={hideToolbar}
                                dods={this.props.dods}
                                dodsById={this.props.dodsById}
                                siteFacilities={this.props.siteFacilities}
                                hasWritePermissions={this.props.hasWritePermissions}
                                processErrors={this.props.processErrors}
                                highlightVariableName={this.props.highlightVariableName}
                            />
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            );

        } else {
            return <div />;
        }
    }

    renderToolbar() {
        let entityHeader = null;
        if (this.props.hideToolbar) {
            entityHeader = (
                <EntityHeader
                    title={'Retrieval Rules'}
                    className="noshrink"
                    infoboxMessage={InfoMessages.retrievalRules}
                    selectedItems={this.getSelectedItems()}
                />
            );
        } else {
            entityHeader = (
                <EntityHeader
                    title={'Retrieval Rule Sets'}
                    className="noshrink"
                    infoboxMessage={InfoMessages.retrievalRules}
                    selectedItems={this.state.selectedItems}
                    count={this.rowData.length}
                >
                    <Toolbar>
                        <ToolbarItem
                            title="Delete"
                            className="fa fa-times delete"
                            clickHandler={this.onDeleteInputDatasetsClick}
                            selectionDependent
                            enabled={this.props.hasWritePermissions}
                        />
                        <ToolbarItem
                            title="Add Rule Set"
                            className="fa fa-plus new"
                            clickHandler={this.onImportWizClick}
                            enabled={this.props.hasWritePermissions}
                            showText
                        />
                    </Toolbar>
                </EntityHeader>
            );
        }
        if (!this.props.process.variable_retrieval.input_datasets ||
            Object.keys(this.props.process.variable_retrieval.input_datasets).length === 0) {
            return (
                <Flexbox flexDirection="column">
                    {entityHeader}
                    <p className="invalid error">At least one retrieval rule must be defined.</p>
                </Flexbox>
            );
        } else {
            return entityHeader;
        }
    }

    getRowKey() {
        return 'name';
    }

    /**
     * Must be defined as an array of objects (not a map)
     */
    getTableRowData(props) {
        return props.inputDatasetNames.map((inputDatasetName) => {
            return props.process.variable_retrieval.input_datasets[inputDatasetName];
        });
    }

    getTableColumns() {
        const collapsedProps =
            {
                style: {
                    width: 85,
                    backgroundColor: '#194173'
                }
            };

        const nameColumn = {
            property: 'name',
            header: {
                label: 'Name',
                formatters: [
                    label => { return this.renderExpandableLabel(this.COL_NAME, label); }
                ],
                transforms: [
                    label => ({
                        onClick: () => this.toggleExpand(this.COL_NAME)
                    })
                ]
            },
            cell: {
                formatters: [
                    name => { return this.renderNameCell(name); }
                ]
            }
        };
        if (!this.state.expandedColumns[this.COL_NAME]) {
            nameColumn.props = collapsedProps;
        }

        const rulesColumn =
            {
                property: 'name',
                header: {
                    label: 'Rules',
                    formatters: [
                        label => { return this.renderExpandableLabel(this.COL_RULES, label); }
                    ],
                    transforms: [
                        label => ({
                            onClick: () => this.toggleExpand(this.COL_RULES)
                        })
                    ]
                },
                cell: {
                    formatters: [
                        name => { return this.renderRulesTable(name); }
                    ]
                }
            };
        if (!this.state.expandedColumns[this.COL_RULES]) {
            rulesColumn.props = collapsedProps;
        }
        const mappingsColumn =
            {
                property: 'name',
                header: {
                    label: 'Variable Name in Input Datastream',
                    formatters: [
                        label => { return this.renderExpandableLabel(this.COL_MAPPINGS, label); }
                    ],
                    transforms: [
                        label => ({
                            onClick: () => this.toggleExpand(this.COL_MAPPINGS)
                        })
                    ]
                },
                cell: {
                    formatters: [
                        name => { return this.renderMappingsTable(name); }
                    ]
                }
            };
        if (!this.state.expandedColumns[this.COL_MAPPINGS]) {
            mappingsColumn.props = collapsedProps;
        }

        return [nameColumn, rulesColumn, mappingsColumn];

    }

}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/inputs/RetrievalRulesTable.js